import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, DataTable, EmptyState, FilterBar, FilterChips, IconButton, Pagination, Pill, SelectField,
  type Column, fmtBRL, fmtDataISO,
} from "@atlas/ui/web";
import { atlas, enterImpersonate } from "../../../lib/sdk";
import { QUERY_UNIDADES_OPERAVEIS, listarUnidadesOperaveis, rotuloUnidade } from "../../../lib/unidades-operaveis";
import { downloadCsv } from "../../../lib/csv";
import { matchAnyKeys } from "../../../lib/text-search";
import { aplicarFiltros, contagens, motivoVazio, POR_PAGINA_PADRAO, recortar } from "../../../lib/recorte";
import { useFiltros } from "../../../lib/use-filtros";
import type { AdminServidor, ServidorCampoConfig } from "@atlas/sdk";
import { rotuloDaColunaSalario } from "../../../lib/regra-margem-tela";
import { EditModal } from "./_editModal";
import { ServidorDetalheModal } from "../../../components/ServidorDetalheModal";
import { DEFAULT_CAMPOS_FALLBACK } from "./_defaults";
import { isSuperAdmin, readAverbadoraPermissoesFromJwt } from "../../../lib/averbadora-perms";

// Salario pro CSV em formato BR (sem "R$"): 3692.3 -> "3.692,30". Bate com o
// parser de import (milhar = ponto, decimal = virgula). Antes o export soltava
// "3692.3" e o import, removendo os pontos, lia 36923 — salario errado no
// round-trip export->import.
const fmtMoedaCsv = (n: number): string => {
  const [int, dec] = Math.abs(n).toFixed(2).split(".");
  const milhar = int!.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${n < 0 ? "-" : ""}${milhar},${dec}`;
};
const fmtCpf = (cpf: string) => {
  const d = (cpf ?? "").replace(/\D/g, "");
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : (cpf || "—");
};
const fmtTel = (tel?: string) => {
  const d = (tel ?? "").replace(/\D/g, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d || "—";
};


/** Extrai o valor de um servidor pra qualquer key (built-in ou custom). */
function getValor(s: AdminServidor, key: string): string | number | null | undefined {
  if (key === "cpf") return s.cpf;
  if (key.startsWith("custom_")) return s.camposCustom?.[key];
  return (s as unknown as Record<string, string | number | null | undefined>)[key];
}

/** Converte um campo da config em Column<AdminServidor> com render por tipo. */
/** Renderiza a coluna de acoes. Botao "Entrar como" (🎭) so aparece pro super
 *  admin — operadores criados pelo super admin com acesso a "servidores" veem
 *  apenas o botao Editar (cliente 28/07/2026). Backend tambem enforce via
 *  requireSuperAdmin no /v1/admin/impersonate/servidor/:matricula. */
function AcoesCell({ servidor, onVer, onEdit, podeImpersonar }: { servidor: AdminServidor; onVer: (s: AdminServidor) => void; onEdit: (s: AdminServidor) => void; podeImpersonar: boolean }) {
  const [impersonando, setImpersonando] = useState(false);
  const entrar = async () => {
    setImpersonando(true);
    try {
      const r = await atlas.admin.impersonateServidor(servidor.matricula);
      enterImpersonate({
        parentRole: "averbadora",
        novoAccessToken: r.access_token,
        novoRefreshToken: r.refresh_token,
        servidor: { nome: r.user.nome, matricula: r.user.matricula, cpfMasked: r.user.cpfMasked },
      });
      window.location.assign("/servidor");
    } catch (e) {
      setImpersonando(false);
      alert(`Erro ao entrar como servidor: ${(e as Error).message}`);
    }
  };
  return (
    <div style={{ display: "inline-flex", gap: 4 }}>
      {podeImpersonar ? (
        <IconButton title={`Entrar como ${servidor.nome}`} onClick={() => { if (!impersonando) void entrar(); }}>
          {impersonando ? "…" : "🎭"}
        </IconButton>
      ) : null}
      {/* 👁 antes do ✎, na mesma ordem de bancos e de órgãos e entidades:
          consultar vem antes de alterar. */}
      <IconButton title="Visualizar" onClick={() => onVer(servidor)}>👁</IconButton>
      <IconButton title="Editar" onClick={() => onEdit(servidor)}>✎</IconButton>
    </div>
  );
}

function campoParaColuna(
  campo: ServidorCampoConfig,
  onVer: (s: AdminServidor) => void,
  onEdit: (s: AdminServidor) => void,
  podeImpersonar: boolean,
  convenioNomePorId: Map<string, string>,
): Column<AdminServidor> {
  if (campo.key === "acoes") {
    return { key: "acoes", header: "", render: (s) => <AcoesCell servidor={s} onVer={onVer} onEdit={onEdit} podeImpersonar={podeImpersonar} /> };
  }
  const base: Column<AdminServidor> = {
    key: campo.key,
    header: campo.label,
    // Sem wrap — a tabela mantem scroll horizontal, mesmo padrao da tela antiga.
    // mono desligado (cliente 24/07/2026): tabela usa fonte unica (sans-serif do
    // sistema) em todas as colunas. Antes matricula/cpf saiam em
    // monospace, o que ficava com estetica diferente do resto.
  };
  base.render = (s) => {
    const v = getValor(s, campo.key);
    const dim = <span style={{ color: "var(--text-dim)" }}>—</span>;
    // Vazio/nulo -> "—". Numero==0 tambem trata como vazio (IBGE 0 = nao veio
    // no CSV, salario 0 = nao informado). Mesma regra do servidores.tsx antigo.
    if (v == null || v === "") return dim;
    if (campo.tipo === "numero" && Number(v) === 0) return dim;
    if (campo.tipo === "moeda" && Number(v) === 0) return dim;
    if (campo.key === "cpf") return <>{fmtCpf(String(v))}</>;
    // Telefone: mesma mascara BR da prefeitura/servidores pra a tabela ficar
    // visualmente identica nos dois perfis.
    if (campo.key === "telefone" || campo.tipo === "telefone") return <>{fmtTel(String(v))}</>;
    if (campo.key === "status") {
      const st = String(v) as AdminServidor["status"];
      return <Pill variant={st === "ativo" ? "averbado" : st === "bloqueado" ? "rejeitada" : "expirado"}>{st}</Pill>;
    }
    // Situacao funcional: Pill verde/pendente/expirada. Mesmo padrao visual
    // da prefeitura/servidores — cliente pediu 24/07/2026 pra alinhar.
    if (campo.key === "situacaoFuncional") {
      const situ = String(v).trim().toUpperCase();
      if (!situ) return <span style={{ color: "var(--text-dim)", fontSize: 12 }}>NÃO INFORMADO</span>;
      const variant: "expirado" | "averbado" | "pendente" =
        /desligado|aposentad/i.test(situ) ? "expirado"
        : /afastad|licenc|ferias|cedid/i.test(situ) ? "pendente"
        : "averbado";
      return <Pill variant={variant}>{situ}</Pill>;
    }
    switch (campo.tipo) {
      case "moeda":
        return typeof v === "number" ? fmtBRL(v) : fmtBRL(Number(v) || 0);
      case "numero":
        return String(v);
      // Data ISO (YYYY-MM-DD) -> DD/MM/YYYY. Cobre dataAdmissao/dataNascimento
      // e qualquer custom com tipo="data". Cliente 28/07/2026.
      case "data":
        return fmtDataISO(String(v));
      default:
        return String(v);
    }
  };
  if (campo.tipo === "moeda" || campo.tipo === "numero") base.align = "right";
  return base;
}

export function AdminServidoresVisualizar() {
  const qc = useQueryClient();
  const prefeituras = useQuery({ queryKey: QUERY_UNIDADES_OPERAVEIS, queryFn: listarUnidadesOperaveis });
  const [prefId, setPrefId] = useState<string>("");
  const [search, setSearch] = useState("");
  // SITUACAO FUNCIONAL — o inventario declarava esta dimensao e a tela nunca a
  // renderizou (era uma das tres declaracoes falsas que a auditoria achou). As
  // opcoes saem do DADO, nao de um enum: `situacaoFuncional` e' string livre
  // que o import por CSV faz cast, nao validacao, entao uma base cujos
  // servidores estao todos como "TRABALHANDO" mostraria zero em "ATIVO".
  const [situacaoFiltro, setSituacaoFiltro] = useState("");
  const [editing, setEditing] = useState<AdminServidor | null>(null);
  const [vendo, setVendo] = useState<AdminServidor | null>(null);
  const [limparAberto, setLimparAberto] = useState(false);

  const prefIdNum = Number(prefId);
  const prefSelecionada = (prefeituras.data?.prefeituras ?? []).find((p) => String(p.id) === prefId);

  const configQ = useQuery({
    queryKey: ["admin", "servidor-campos-config", prefIdNum],
    queryFn: () => atlas.admin.getServidorCamposConfig(prefIdNum),
    enabled: !!prefId,
  });

  const dataQ = useQuery({
    queryKey: ["admin", "servidores", prefId],
    queryFn: () => atlas.admin.listServidores({ prefeitura_id: prefIdNum }),
    enabled: !!prefId,
  });

  // Lookup nome do convenio pelo ID (pra tabela renderizar legivel em vez de
  // "CONV-001"). staleTime alto: convenio muda raramente. Cliente 28/07/2026.
  const conveniosQ = useQuery({
    queryKey: ["admin", "convenios"],
    queryFn: () => atlas.admin.listConvenios(),
    staleTime: 5 * 60_000,
  });
  const convenioNomePorId = useMemo(() => {
    const m = new Map<string, string>();
    for (const cv of conveniosQ.data?.convenios ?? []) m.set(cv.id, cv.nome);
    return m;
  }, [conveniosQ.data?.convenios]);

  // Margem e contratos do servidor aberto no 👁. Só o que a LINHA não tem — o
  // cadastro já veio inteiro na listagem. `prefIdNum` é obrigatório: matrícula
  // é numerada por folha, e sem ele a rota recusa por ambiguidade.
  const detalheQ = useQuery({
    queryKey: ["admin", "servidor-detalhe", prefId, vendo?.matricula],
    queryFn: () => atlas.admin.servidorDetalhe(vendo!.matricula, prefIdNum),
    enabled: !!vendo && !!prefId,
  });

  // BUSCA COM CHAVES EXPLICITAS desde 04/09/2026. Era `matchAny`, que varre
  // TODOS os primitivos da linha — e nesta tela isso significava salario,
  // endereco, telefone, IBGE e datas. Consequencias reais: digitar "1000"
  // trazia quem ganha R$ 1.000; digitar um pedaco de CEP trazia o bairro
  // inteiro; e o operador nunca sabia POR QUE uma linha casou.
  //
  // Agora cobre nome, CPF (com e sem mascara) e matricula — as tres chaves que
  // `AdminServidor` de fato tem. ID unico NAO entra porque nao existe nesta
  // linha: prometer no placeholder o que o dado nao tem e' o mesmo defeito da
  // caixa de busca morta, só mais dificil de notar. A normalizacao (caixa,
  // acento, digitos) continua a de `@atlas/types/busca`, entao
  // "580.886.363-53" segue achando com "58088".
  //
  // Ordem "mais recente no topo" vem do BACKEND (criadoEmIso/_dbId).
  const termo = search.trim();
  const situacaoDe = (s: AdminServidor) => String(s.situacaoFuncional ?? "").trim();

  const predicados = useMemo(() => ({
    situacao: situacaoFiltro
      ? (s: AdminServidor) => situacaoDe(s) === situacaoFiltro
      : null,
    busca: termo
      ? (s: AdminServidor) => matchAnyKeys(s, termo, ["nome", "cpf", "cpfMasked", "matricula"])
      : null,
  }), [situacaoFiltro, termo]);

  const todos: AdminServidor[] = dataQ.data?.servidores ?? [];
  const filtered = useMemo(() => aplicarFiltros(todos, predicados), [todos, predicados]);
  const semSituacao = useMemo(() => aplicarFiltros(todos, predicados, "situacao"), [todos, predicados]);
  const contSituacao = useMemo(() => contagens(semSituacao, situacaoDe), [semSituacao]);


  // Esta tela tinha paginação PRÓPRIA — e era dela que o componente
  // `Pagination` de @atlas/ui foi extraído, sem nunca voltar aqui. Duas
  // implementações do mesmo comportamento divergem com o tempo; esta já
  // divergia (opções 25/50/100/200 contra 10/25/50/100 do resto do produto).
  // Agora usa o mesmo controle e o mesmo cálculo que as demais grids.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(POR_PAGINA_PADRAO);
  useEffect(() => { setPage(1); }, [search, prefId, pageSize]);

  const filtros = useFiltros(
    [
      // Prefeitura e' PRE-CONDICAO, nao filtro: sem ela nao ha schema de
      // colunas nem dado. Nao conta no contador e o reset nao mexe — limpar
      // esvaziaria a tela em vez de ampliar o recorte.
      { nome: "prefeitura", ligado: prefId !== "", contexto: true, limpar: () => {} },
      { nome: "busca", ligado: search !== "", limpar: () => setSearch("") },
      { nome: "situacao", ligado: situacaoFiltro !== "", limpar: () => setSituacaoFiltro("") },
    ],
    setPage,
  );
  const pagina = recortar(filtered, page, pageSize);
  const total = pagina.total;
  const pageRows = pagina.itens;

  // Le permissoes do JWT uma vez (novo token = novo mount da tela apos refresh).
  // O super-admin vem com ["*"]; isSuperAdmin ainda trata JWT sem a claim
  // (sessao antiga) como super.
  const podeImpersonar = useMemo(() => {
    try {
      const stored = window.localStorage.getItem("atlas:tokens");
      const token = stored ? JSON.parse(stored)?.access_token : null;
      return isSuperAdmin(readAverbadoraPermissoesFromJwt(token));
    } catch { return false; }
  }, []);

  // Com `margem_da_folha`, a coluna do salario traz a margem de emprestimo que a
  // folha mandou (Caucaia): tabela e ficha dizem isso, em vez de "Salario liquido".
  const regraMargem = dataQ.data?.regraMargem;
  const rotular = useMemo(
    () => <C extends { key: string; label: string }>(c: C): C =>
      c.key === "salarioLiquido" ? { ...c, label: rotuloDaColunaSalario(regraMargem, c.label) } : c,
    [regraMargem],
  );

  // Os campos do CADASTRO na ficha do 👁: os mesmos visíveis da tabela, sem a
  // coluna de ações (que não é dado do servidor).
  const camposDaFicha = useMemo(() => {
    const config = configQ.data?.config;
    const base = config
      ? config.campos.filter((c) => c.visivel).sort((a, b) => a.ordem - b.ordem)
      : DEFAULT_CAMPOS_FALLBACK;
    return base
      .filter((c) => c.key !== "acoes")
      .map((c) => rotular({ key: c.key, label: c.label, tipo: c.tipo }));
  }, [configQ.data?.config, rotular]);

  const valoresDaFicha = (s: AdminServidor): Record<string, string | number | null | undefined> =>
    Object.fromEntries(camposDaFicha.map((c) => [c.key, getValor(s, c.key)]));

  const columns: Column<AdminServidor>[] = useMemo(() => {
    const config = configQ.data?.config;
    const celulaAcoes = (s: AdminServidor) => (
      <AcoesCell servidor={s} onVer={setVendo} onEdit={setEditing} podeImpersonar={podeImpersonar} />
    );
    // Fallback: se o endpoint de config nao existe ainda (backend nao deployado)
    // ou falhou, monta colunas built-in default equivalentes ao antigo servidores.tsx.
    // Assim a tela nao aparece em branco enquanto a API nao tem os novos endpoints.
    if (!config) {
      return DEFAULT_CAMPOS_FALLBACK
        .map((c) => campoParaColuna(rotular(c), setVendo, setEditing, podeImpersonar, convenioNomePorId))
        .concat([{ key: "acoes", header: "", render: celulaAcoes }]);
    }
    const visiveis = config.campos.filter((c) => c.visivel).sort((a, b) => a.ordem - b.ordem);
    const cols = visiveis.map((c) => campoParaColuna(rotular(c), setVendo, setEditing, podeImpersonar, convenioNomePorId));
    cols.push({ key: "acoes", header: "", render: celulaAcoes });
    return cols;
  }, [configQ.data?.config, podeImpersonar, convenioNomePorId, rotular]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Averbadora
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Ver servidores</h1>
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
              {prefId ? (dataQ.isLoading ? "Carregando…" : `${total} servidor${total === 1 ? "" : "es"}`) : "Selecione uma prefeitura"}
            </span>
          </div>
          {/* Limpar linhas de teste/ficcao ("ZZ OBSOLETA - NAO USAR") — abre modal
              que pede a senha (ADMIN_PURGE_PASSWORD do env). Quem executa e' o
              usuario digitando a senha aqui; destrava o kill-switch + apaga por
              nome (so a ficcao sai, os reais ficam). Cliente pediu 24/07/2026. */}
          <Button variant="ghost" onClick={() => setLimparAberto(true)}>Limpar Servidores</Button>
        </div>
      </header>
      {limparAberto ? <LimparObsoletosModal onClose={() => setLimparAberto(false)} onDone={() => { setLimparAberto(false); qc.invalidateQueries({ queryKey: ["admin"] }); qc.invalidateQueries({ queryKey: ["prefeitura"] }); }} /> : null}

      <FilterBar
        searchValue={search}
        onSearchChange={filtros.filtrar(setSearch)}
        searchPlaceholder="Buscar por nome, CPF ou matrícula"
        // O reset ANTIGO limpava a prefeitura junto, e isso esvaziava a tela:
        // sem prefeitura nao ha schema nem dado. Agora ela e' `contexto` no
        // `useFiltros` e sobrevive ao reset.
        {...filtros.propsFilterBar}
        onExport={total > 0 ? () => downloadCsv(
          `servidores-${prefSelecionada?.nome ?? "todos"}.csv`,
          filtered.map((s) => {
            const row: Record<string, string | number> = {};
            const config = configQ.data?.config;
            const visiveis = config ? config.campos.filter((c) => c.visivel).sort((a, b) => a.ordem - b.ordem) : [];
            for (const c of visiveis) {
              const v = getValor(s, c.key);
              if (v == null || v === "") { row[c.key] = ""; continue; }
              if (c.tipo === "moeda") {
                // Sempre BR "3.692,30" pra o import ler certo no round-trip.
                const n = typeof v === "number" ? v : Number(String(v).replace(/[^\d,.-]/g, "").replace(/\./g, "").replace(",", "."));
                row[c.key] = Number.isFinite(n) ? fmtMoedaCsv(n) : String(v);
              } else {
                row[c.key] = typeof v === "number" ? v : String(v);
              }
            }
            return row;
          }),
        ) : undefined}
      >
        {/* PREFEITURA E' CONTEXTO OBRIGATORIO, nao filtro: sem ela nao ha
            schema de colunas nem dado. Fica no inicio da barra, e o `useFiltros`
            a declara com `contexto: true` — nao conta no contador e o reset nao
            a limpa, porque limpar esvaziaria a tela em vez de ampliar o
            recorte. */}
        <div style={{ maxWidth: 320 }}>
          <SelectField
            label="Prefeitura"
            value={prefId}
            onChange={(e) => setPrefId(e.target.value)}
            options={[{ value: "", label: "— Selecione —" }, ...((prefeituras.data?.prefeituras ?? []).map((p) => ({ value: String(p.id), label: rotuloUnidade(p) })))]}
          />
        </div>
        {/* Chips so' com mais de uma situacao presente — um controle que nao
            corta nada e' ruido. */}
        {prefId && Object.keys(contSituacao).length > 1 ? (
          <FilterChips
            label="Situação funcional"
            value={situacaoFiltro}
            onChange={filtros.filtrar(setSituacaoFiltro)}
            options={[
              { value: "", label: "Todas", count: semSituacao.length },
              ...Object.entries(contSituacao)
                .sort(([a], [b]) => a.localeCompare(b, "pt-BR"))
                .map(([valor, n]) => ({ value: valor, label: valor, count: n })),
            ]}
          />
        ) : null}
      </FilterBar>

      {!prefId ? (
        <div style={{ padding: 36, textAlign: "center", color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 12 }}>
          Selecione uma prefeitura acima para listar os servidores.
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={pageRows}
            rowKey={(s) => String(s.id)}
            loading={dataQ.isLoading || configQ.isLoading}
            emptyState={
              dataQ.isError
                ? "Não foi possível carregar os servidores desta prefeitura."
                : <EmptyState
                    motivo={motivoVazio(todos.length, filtered.length)}
                    filtroVazio="Nenhum servidor bate com esse recorte."
                    semDados="Nenhum servidor nesta prefeitura. A carga inicial é feita no onboarding."
                    onLimpar={filtros.limparFiltros}
                  />
            }
          />

          <Pagination
            total={total}
            pagina={pagina.pagina}
            porPagina={pageSize}
            onPagina={setPage}
            onPorPagina={setPageSize}
            rotulo="servidores"
          />
        </>
      )}

      {editing ? (
        <EditModal
          servidor={editing}
          unidadeId={prefIdNum}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["admin", "servidores"] }); }}
        />
      ) : null}

      {vendo ? (
        <ServidorDetalheModal
          nome={vendo.nome}
          // A MESMA config que ordena a tabela: ficha e tabela nunca discordam
          // de rótulo, ordem ou formato. Sem config (backend antigo), o fallback
          // é o mesmo que a tabela usa.
          campos={camposDaFicha}
          valores={valoresDaFicha(vendo)}
          detalhe={detalheQ.data}
          carregando={detalheQ.isLoading}
          erro={detalheQ.isError}
          carregarHistorico={(adf) => atlas.admin.historicoPagamentosContrato(adf)}
          onClose={() => setVendo(null)}
        />
      ) : null}
    </div>
  );
}

/** Modal pra apagar as linhas de teste/ficcao "ZZ OBSOLETA - NAO USAR".
 *  Pede a senha (ADMIN_PURGE_PASSWORD do env). O fluxo: destrava o kill-switch
 *  com a senha, depois apaga por NOME (so a ficcao; os reais com a mesma
 *  matricula ficam). Quem executa e' o usuario digitando a senha aqui. */
function LimparObsoletosModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const NOME_ALVO = "ZZ OBSOLETA - NAO USAR";
  const [senha, setSenha] = useState("");
  const [show, setShow] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const executar = async () => {
    if (!senha) { setErro("Informe a senha."); return; }
    setEnviando(true);
    setErro(null);
    try {
      // 1) destrava o kill-switch com a senha; 2) apaga por nome.
      await atlas.admin.destravarDestrutivos(senha, 120);
      const r = await atlas.admin.deleteServidoresPorNome([NOME_ALVO], senha);
      alert(`Pronto. ${r.removidos} servidor(es) "${NOME_ALVO}" removido(s).`);
      onDone();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Falha ao remover.";
      setErro(/senha inv/i.test(msg) ? "Senha incorreta." : msg);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <div
      onMouseDown={enviando ? undefined : (e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}
    >
      <div onMouseDown={(e) => e.stopPropagation()} style={{ background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 16, padding: 24, maxWidth: 460, width: "100%", display: "flex", flexDirection: "column", gap: 14 }}>
        <h3 style={{ margin: 0, color: "var(--danger-500)" }}>⚠ Remover linhas de teste</h3>
        <p style={{ color: "var(--text-muted)", fontSize: 14, margin: 0 }}>
          Apaga <b style={{ color: "var(--danger-500)" }}>todos</b> os servidores com nome <b>"{NOME_ALVO}"</b>. Os servidores reais (mesmo com matrícula igual) NÃO são afetados — a exclusão é pelo nome. Ação irreversível.
        </p>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase", display: "block", marginBottom: 6 }}>
            Senha de confirmação <span style={{ color: "var(--danger-500)" }}>*</span>
          </label>
          <div style={{ position: "relative" }}>
            <input
              type={show ? "text" : "password"}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !enviando) executar(); }}
              autoFocus
              placeholder="ADMIN_PURGE_PASSWORD (do env)"
              style={{ width: "100%", boxSizing: "border-box", background: "var(--bg-elev)", border: `1px solid ${erro ? "var(--danger-500)" : "var(--border-strong)"}`, borderRadius: 10, padding: "10px 40px 10px 14px", color: "var(--text)", fontSize: 14, outline: "none" }}
            />
            <button type="button" onClick={() => setShow((s) => !s)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-dim)", fontSize: 13 }} aria-label={show ? "Ocultar senha" : "Mostrar senha"}>{show ? "🙈" : "👁"}</button>
          </div>
          {erro ? <span style={{ fontSize: 12, color: "var(--danger-500)", marginTop: 4, display: "block" }}>{erro}</span> : null}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={executar} disabled={enviando}>{enviando ? "Removendo…" : "Remover"}</Button>
        </div>
      </div>
    </div>
  );
}
